import { useRef } from "react";

import { Icon } from "@/shared/components/common/Icon/Icon";

import { getAsyncOptionsState } from "../asyncOptions";
import { handleDropdownNavigationKey } from "../dropdownKeyboard";
import { FieldError, FieldRetryButton, IconOrSpinner } from "../FieldParts";
import { OptionList } from "../OptionList";
import { PortalDropdown } from "../PortalDropdown";
import {
  ChevronSlot,
  Container,
  Label,
  OptionItem,
  SelectWrapper,
  Trigger,
  TriggerText,
} from "../shared.styles";
import { getOptionText } from "../types";
import { useDropdown } from "../useDropdown";
import { useSubmitFirstField } from "../useSubmitFirstField";

import type { AsyncOptionsProps, FieldBaseProps, Option } from "../types";

type SelectBoxBaseProps<T> = FieldBaseProps &
  AsyncOptionsProps & {
    options: Option<T>[];
    value: T;
    onChange?: (value: T) => void;
    /** Sits in a filter bar rather than a form: shrinks to its text and wears the outlined buttons' border. */
    toolbar?: boolean;
  };

const SelectBoxBase = <T,>({
  disabled,
  error,
  fetchError,
  fullWidth,
  label,
  loading,
  name,
  onBlur,
  onChange,
  onRetry,
  options,
  placeholder,
  toolbar,
  value,
}: SelectBoxBaseProps<T>): React.ReactElement => {
  const { displayedError, isDisabled, retry } = getAsyncOptionsState({
    disabled,
    error,
    fetchError,
    loading,
    onRetry,
  });
  const typeaheadRef = useRef("");
  const typeaheadTimeoutRef = useRef<ReturnType<typeof setTimeout>>();

  const {
    close,
    collapse,
    dropdownRef,
    focusedIndex,
    focusedOptionId,
    isOpen,
    open: openDropdown,
    setFocusedIndex,
    wrapperRef,
  } = useDropdown({ name, onClose: onBlur });

  const selectedIndex = options.findIndex((opt) => opt.value === value);
  const selectedOption =
    selectedIndex >= 0 ? options[selectedIndex] : undefined;

  const open = (): void => openDropdown(selectedIndex >= 0 ? selectedIndex : 0);

  const handleSelect = (optionValue: T): void => {
    onChange?.(optionValue);
    collapse();
  };

  const handleTriggerClick = () => {
    if (isOpen) {
      close();
    } else {
      open();
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    const handled = handleDropdownNavigationKey(e, {
      isDisabled,
      isOpen,
      itemCount: options.length,
      onEscape: close,
      onTab: close,
      open,
      setFocusedIndex,
    });
    if (handled) return;

    switch (e.key) {
      // Component-specific: Enter/Space selects the highlighted option (or opens).
      case "Enter":
      case " ": {
        e.preventDefault();
        if (isOpen && focusedIndex >= 0) {
          handleSelect(options[focusedIndex].value);
        } else if (!isOpen) {
          open();
        }
        break;
      }
      // Component-specific: type-ahead jump to a matching option.
      default: {
        if (e.key.length === 1 && !e.ctrlKey && !e.metaKey && !e.altKey) {
          clearTimeout(typeaheadTimeoutRef.current);
          typeaheadRef.current += e.key.toLowerCase();

          const matchIndex = options.findIndex((opt) =>
            getOptionText(opt).toLowerCase().startsWith(typeaheadRef.current),
          );

          if (matchIndex >= 0) {
            if (!isOpen) open();
            setFocusedIndex(matchIndex);
          }

          typeaheadTimeoutRef.current = setTimeout(() => {
            typeaheadRef.current = "";
          }, 500);
        }
        break;
      }
    }
  };

  return (
    <Container $fullWidth={fullWidth} $toolbar={toolbar}>
      {label && (
        <Label htmlFor={`${name}-select`} id={`${name}-label`}>
          {label}
        </Label>
      )}
      <SelectWrapper ref={wrapperRef}>
        <Trigger
          $disabled={!!isDisabled}
          $error={!!displayedError}
          $isOpen={isOpen}
          $toolbar={toolbar}
          aria-activedescendant={focusedOptionId}
          aria-expanded={isOpen}
          aria-haspopup="listbox"
          data-testid={name}
          disabled={isDisabled}
          id={`${name}-select`}
          onClick={handleTriggerClick}
          onKeyDown={handleKeyDown}
          type="button"
        >
          <TriggerText $hasValue={!!selectedOption}>
            {selectedOption ? selectedOption.label : placeholder}
          </TriggerText>
          <ChevronSlot $disabled={!!isDisabled} $isOpen={isOpen}>
            <IconOrSpinner icon={<Icon name="caret" />} loading={loading} />
          </ChevronSlot>
        </Trigger>
        {isOpen && (
          <PortalDropdown anchorRef={wrapperRef} dropdownRef={dropdownRef}>
            <OptionList
              aria-labelledby={label ? `${name}-label` : undefined}
              count={options.length}
              focusedIndex={focusedIndex}
              role="listbox"
              renderOption={(index, measureRef) => {
                const opt = options[index];
                return (
                  <OptionItem
                    $isFocused={index === focusedIndex}
                    $isSelected={opt.value === value}
                    aria-selected={opt.value === value}
                    data-index={index}
                    data-testid={`${name}-option-${index}`}
                    id={`${name}-option-${index}`}
                    key={String(opt.value)}
                    onClick={() => handleSelect(opt.value)}
                    onMouseEnter={() => setFocusedIndex(index)}
                    ref={measureRef}
                    role="option"
                  >
                    {opt.label}
                  </OptionItem>
                );
              }}
            />
          </PortalDropdown>
        )}
      </SelectWrapper>
      <FieldError error={displayedError} name={name} />
      {retry && <FieldRetryButton name={name} onClick={retry} />}
    </Container>
  );
};

type SelectBoxProps<T> = AsyncOptionsProps & {
  label: React.ReactNode;
  name: string;
  options: Option<T>[];
  disabled?: boolean;
  fullWidth?: boolean;
  placeholder?: string;
  onValueChange?: (value: T) => void;
};

const SelectBox = <T,>({
  name,
  onValueChange,
  ...props
}: SelectBoxProps<T>): React.ReactElement => {
  const { error, handleBlur, setValue, value } = useSubmitFirstField<T>(name);

  const handleChange = (next: T): void => {
    setValue(next);
    onValueChange?.(next);
  };

  return (
    <SelectBoxBase
      {...props}
      error={error}
      name={name}
      onBlur={handleBlur}
      onChange={handleChange}
      value={value}
    />
  );
};

export { SelectBox, SelectBoxBase };
export type { Option };
